import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Sparkle, Image as ImgIcon, FilmStrip, ListMagnifyingGlass } from "@phosphor-icons/react";

const CATS = ["travel-hacks","points-miles","airlines","hotels","destinations","reviews","credit-cards","videos","deals"];
const STATUSES = ["idea","queued","drafting","draft_ready","in_review","scheduled","published","rejected"];

export default function ArticleEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState({
    title: "", slug: "", excerpt: "", body_rich_text: "", category: "travel-hacks",
    tags: [], featured_image: "", author: "", status: "draft_ready", source_type: "human",
    seo_title: "", meta_description: "", faq_items: [], affiliate_embed_blocks: [],
    affiliate_disclosure_enabled: true, newsletter_cta_enabled: true, auto_publish: false, is_featured: false,
    publish_at: "", reviewer: "",
  });
  const [cards, setCards] = useState([]);
  const [aiBusy, setAiBusy] = useState(""); // current AI task
  const [schedulerTime, setSchedulerTime] = useState("");

  useEffect(() => {
    api.get("/cards?active_only=true&limit=100").then((r) => setCards(r.data.items));
    if (id && id !== "new") {
      api.get(`/articles/${id}`).then((r) => setA({ ...r.data, tags: r.data.tags || [], faq_items: r.data.faq_items || [], affiliate_embed_blocks: r.data.affiliate_embed_blocks || [] }));
    }
  }, [id]);

  const update = (patch) => setA((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    try {
      if (id && id !== "new" && a.id) {
        await api.put(`/articles/${a.id}`, a);
        toast.success("Saved");
      } else {
        const { data } = await api.post("/articles", a);
        toast.success("Created");
        nav(`/admin/articles/${data.id}`, { replace: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const publishNow = async () => {
    await save();
    if (!a.id) return;
    try { await api.post(`/articles/${a.id}/publish`); toast.success("Published"); update({ status: "published" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };

  const schedule = async () => {
    if (!schedulerTime) return toast.error("Pick a date/time");
    await save();
    if (!a.id) return;
    try { await api.post(`/articles/${a.id}/schedule`, { publish_at: new Date(schedulerTime).toISOString() }); toast.success("Scheduled"); update({ status: "scheduled", publish_at: new Date(schedulerTime).toISOString() }); }
    catch (e) { toast.error("Schedule failed"); }
  };

  const runAi = async (kind) => {
    setAiBusy(kind);
    try {
      if (kind === "brief") {
        const { data } = await api.post("/ai/brief", { topic: a.title || "Travel hack topic" });
        update({ body_rich_text: (a.body_rich_text || "") + "\n\n<!-- BRIEF -->\n" + data.content });
      } else if (kind === "draft") {
        const { data } = await api.post("/ai/draft", { brief: a.body_rich_text || a.title });
        update({ body_rich_text: data.content, source_type: "ai-assisted" });
      } else if (kind === "faq") {
        const { data } = await api.post("/ai/faq", { article_text: a.body_rich_text });
        try {
          const clean = data.content.replace(/```json|```/g, "").trim();
          const parsed = JSON.parse(clean);
          update({ faq_items: parsed });
        } catch { toast.error("FAQ parse failed — raw result logged"); console.log(data.content); }
      } else if (kind === "seo") {
        const { data } = await api.post("/ai/seo-meta", { article_text: a.body_rich_text });
        try {
          const clean = data.content.replace(/```json|```/g, "").trim();
          const parsed = JSON.parse(clean);
          update({ seo_title: parsed.seo_title, meta_description: parsed.meta_description });
        } catch { toast.error("SEO parse failed"); }
      } else if (kind === "image") {
        const promptRes = await api.post("/ai/image-prompt", { topic: a.title });
        const { data } = await api.post("/ai/image", { prompt: promptRes.data.content });
        update({ featured_image: data.url });
        toast.success("Image generated");
      } else if (kind === "video") {
        const { data } = await api.post("/ai/video-script", { article_text: a.body_rich_text });
        update({ video_embed_url: null });
        toast.success("Video script generated");
        navigator.clipboard?.writeText(data.content);
      } else if (kind === "recommend-cards") {
        const { data } = await api.post("/ai/recommend-cards", { topic: a.title });
        try {
          const clean = data.content.replace(/```json|```/g, "").trim();
          const parsed = JSON.parse(clean);
          const blocks = parsed.map((r, i) => ({
            block_type: i === parsed.length - 1 ? "end_of_article_best_cards" : "single_card_cta",
            card_ids: [r.card_id],
            heading: r.reason?.slice(0, 80),
            position: i * 400,
          }));
          update({ affiliate_embed_blocks: [...a.affiliate_embed_blocks, ...blocks] });
          toast.success(`${blocks.length} affiliate blocks suggested`);
        } catch { toast.error("Parse failed"); }
      }
      toast.success(`${kind} done`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `${kind} failed`);
    } finally { setAiBusy(""); }
  };

  const addBlock = () => update({ affiliate_embed_blocks: [...a.affiliate_embed_blocks, { block_type: "single_card_cta", card_ids: [], heading: "", position: a.affiliate_embed_blocks.length * 200 }] });
  const updBlock = (i, patch) => {
    const next = [...a.affiliate_embed_blocks];
    next[i] = { ...next[i], ...patch };
    update({ affiliate_embed_blocks: next });
  };
  const delBlock = (i) => update({ affiliate_embed_blocks: a.affiliate_embed_blocks.filter((_, j) => j !== i) });

  const AI = ({ id, icon: I, label }) => (
    <button onClick={() => runAi(id)} disabled={aiBusy === id} className="flex items-center gap-2 border border-[#D6CFC4] px-3 py-2 text-xs hover:bg-[#EFECE5] disabled:opacity-50" data-testid={`ai-${id}-btn`}>
      <I size={14} />{aiBusy === id ? "…" : label}
    </button>
  );

  return (
    <div className="p-8 md:p-10 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/admin/articles" className="overline text-[#4A4742]">← Articles</Link>
          <h1 className="font-serif text-3xl mt-1">{id === "new" || !a.id ? "New article" : "Edit article"}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={save} className="bg-[#1B3B36] text-white px-5 py-2.5 text-sm hover:bg-[#112622]" data-testid="save-article-btn">Save</button>
          <button onClick={publishNow} className="bg-[#B4533C] text-white px-5 py-2.5 text-sm hover:bg-[#8F402D]" data-testid="publish-now-btn">Publish now</button>
        </div>
      </div>

      <section className="space-y-5">
        <div>
          <label className="overline">Title</label>
          <input value={a.title} onChange={(e) => update({ title: e.target.value })} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 font-serif text-2xl" data-testid="article-title-input" />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="overline">Slug</label>
            <input value={a.slug} onChange={(e) => update({ slug: e.target.value })} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm font-mono" data-testid="article-slug-input" />
          </div>
          <div>
            <label className="overline">Category</label>
            <select value={a.category} onChange={(e) => update({ category: e.target.value })} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" data-testid="article-category-select">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="overline">Status</label>
            <select value={a.status} onChange={(e) => update({ status: e.target.value })} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" data-testid="article-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="overline">Excerpt</label>
          <textarea value={a.excerpt} onChange={(e) => update({ excerpt: e.target.value })} rows={3} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" data-testid="article-excerpt-input" />
        </div>
        <div>
          <label className="overline">Featured Image URL (or use AI)</label>
          <div className="flex gap-2 mt-1">
            <input value={a.featured_image || ""} onChange={(e) => update({ featured_image: e.target.value })} className="flex-1 border border-[#D6CFC4] px-3 py-2 text-sm font-mono" data-testid="article-image-input" />
          </div>
          {a.featured_image && <img src={a.featured_image} alt="" className="mt-3 w-60 aspect-video object-cover border border-[#D6CFC4]" />}
        </div>
        <div>
          <label className="overline">Body (HTML supported)</label>
          <textarea value={a.body_rich_text} onChange={(e) => update({ body_rich_text: e.target.value })} rows={18} className="w-full border border-[#D6CFC4] px-3 py-3 mt-1 text-sm font-mono" data-testid="article-body-input" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="overline">Author</label>
            <input value={a.author || ""} onChange={(e) => update({ author: e.target.value })} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" data-testid="article-author-input" />
          </div>
          <div>
            <label className="overline">Reviewer</label>
            <input value={a.reviewer || ""} onChange={(e) => update({ reviewer: e.target.value })} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="overline">SEO title</label>
            <input value={a.seo_title || ""} onChange={(e) => update({ seo_title: e.target.value })} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" data-testid="article-seo-title-input" />
          </div>
          <div>
            <label className="overline">Meta description</label>
            <input value={a.meta_description || ""} onChange={(e) => update({ meta_description: e.target.value })} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" data-testid="article-meta-input" />
          </div>
        </div>
        <div>
          <label className="overline">Tags (comma separated)</label>
          <input value={a.tags?.join(", ") || ""} onChange={(e) => update({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" />
        </div>

        <div className="flex gap-5 items-center">
          <label className="flex items-center gap-2 text-sm" data-testid="article-auto-publish"><input type="checkbox" checked={!!a.auto_publish} onChange={(e) => update({ auto_publish: e.target.checked })} /> Auto-publish eligible</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!a.is_featured} onChange={(e) => update({ is_featured: e.target.checked })} /> Featured on homepage</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!a.affiliate_disclosure_enabled} onChange={(e) => update({ affiliate_disclosure_enabled: e.target.checked })} /> Affiliate disclosure</label>
        </div>

        <div className="border-t border-[#D6CFC4] pt-6">
          <div className="overline text-[#B4533C] mb-3">AI Assist</div>
          <div className="flex flex-wrap gap-2">
            <AI id="brief" icon={Sparkle} label="Generate brief" />
            <AI id="draft" icon={Sparkle} label="Generate full draft" />
            <AI id="faq" icon={ListMagnifyingGlass} label="Generate FAQ" />
            <AI id="seo" icon={ListMagnifyingGlass} label="Generate SEO meta" />
            <AI id="image" icon={ImgIcon} label="Generate hero image" />
            <AI id="video" icon={FilmStrip} label="Generate video script" />
            <AI id="recommend-cards" icon={Sparkle} label="Suggest affiliate cards" />
          </div>
        </div>

        <div className="border-t border-[#D6CFC4] pt-6">
          <div className="flex items-center justify-between mb-3">
            <div className="overline text-[#B4533C]">Affiliate Embed Blocks</div>
            <button onClick={addBlock} className="text-xs border border-[#D6CFC4] px-3 py-1.5 hover:bg-[#EFECE5]" data-testid="add-affiliate-block-btn">+ Add block</button>
          </div>
          {a.affiliate_embed_blocks.map((b, i) => (
            <div key={i} className="border border-[#D6CFC4] p-4 mb-3" data-testid={`affiliate-block-${i}`}>
              <div className="grid md:grid-cols-3 gap-3">
                <select value={b.block_type} onChange={(e) => updBlock(i, { block_type: e.target.value })} className="border border-[#D6CFC4] px-2 py-1.5 text-xs">
                  {["single_card_cta","two_card_comparison","three_card_best_for","inline_text_cta","sticky_mobile_card","end_of_article_best_cards"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input value={b.heading || ""} onChange={(e) => updBlock(i, { heading: e.target.value })} placeholder="Heading" className="border border-[#D6CFC4] px-2 py-1.5 text-xs" />
                <input type="number" value={b.position} onChange={(e) => updBlock(i, { position: Number(e.target.value) })} placeholder="Position" className="border border-[#D6CFC4] px-2 py-1.5 text-xs" />
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                {cards.map((c) => {
                  const checked = (b.card_ids || []).includes(c.id);
                  return (
                    <label key={c.id} className={`flex items-center gap-2 border px-2 py-1.5 text-xs cursor-pointer ${checked ? "border-[#1B3B36] bg-[#EFECE5]" : "border-[#D6CFC4]"}`}>
                      <input type="checkbox" checked={checked} onChange={(e) => {
                        const ids = e.target.checked ? [...(b.card_ids || []), c.id] : (b.card_ids || []).filter((x) => x !== c.id);
                        updBlock(i, { card_ids: ids });
                      }} />
                      {c.card_name}
                    </label>
                  );
                })}
              </div>
              <button onClick={() => delBlock(i)} className="mt-3 text-xs text-[#B4533C]">Remove block</button>
            </div>
          ))}
        </div>

        <div className="border-t border-[#D6CFC4] pt-6">
          <div className="overline text-[#B4533C] mb-3">FAQ Items</div>
          {a.faq_items.map((f, i) => (
            <div key={i} className="grid md:grid-cols-2 gap-2 mb-2">
              <input value={f.question} onChange={(e) => { const next = [...a.faq_items]; next[i].question = e.target.value; update({ faq_items: next }); }} placeholder="Question" className="border border-[#D6CFC4] px-2 py-1.5 text-sm" />
              <input value={f.answer} onChange={(e) => { const next = [...a.faq_items]; next[i].answer = e.target.value; update({ faq_items: next }); }} placeholder="Answer" className="border border-[#D6CFC4] px-2 py-1.5 text-sm" />
            </div>
          ))}
          <button onClick={() => update({ faq_items: [...a.faq_items, { question: "", answer: "" }] })} className="text-xs border border-[#D6CFC4] px-3 py-1.5">+ Add FAQ</button>
        </div>

        <div className="border-t border-[#D6CFC4] pt-6">
          <div className="overline text-[#B4533C] mb-3">Scheduled publish</div>
          <div className="flex gap-2">
            <input type="datetime-local" value={schedulerTime} onChange={(e) => setSchedulerTime(e.target.value)} className="border border-[#D6CFC4] px-3 py-2 text-sm" data-testid="schedule-time-input" />
            <button onClick={schedule} className="border border-[#1B3B36] text-[#1B3B36] px-4 py-2 text-sm hover:bg-[#1B3B36] hover:text-white" data-testid="schedule-btn">Schedule</button>
          </div>
          {a.publish_at && <div className="mt-2 text-xs font-mono text-[#4A4742]">Current: {new Date(a.publish_at).toLocaleString()}</div>}
        </div>
      </section>
    </div>
  );
}
